/**
 * useMatrixRooms Hook
 * Custom hook for managing Matrix rooms with loading states and event listeners
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { RoomEvent, RoomStateEvent } from 'matrix-js-sdk';
import Matrix from '../../components/matrix/index.js';
import { captureChatException } from 'shared/sentry';
import {
  convertRoomToUserFormat,
  pruneAbandonedInboxEntries,
  sortRoomsByTimestamp,
  shouldRefreshRoomsForTimelineEvent,
  SYNC_STATE_PREPARED,
  SYNC_STATE_SYNCING,
  SYNC_STATE_CATCHUP
} from './matrixRoomUtils';

/** Inbox spinner cap. Chat/VoIP wait on WAIT_INITIAL_TIMELINE_MS (10s), not this. */
const LOADING_FALLBACK_MAX_WAIT_MS = 8000;

const EMPTY_PREVIEW = 'No messages yet';

const readInbox = () => Matrix.getInboxListSnapshot();

function rememberInbox(live) {
  if (live.length > 0) Matrix.setInboxListSnapshot(live);
}

/** Keep last-message text from the cached inbox until the live timeline has a preview. */
function withCachedPreviews(live, cached) {
  if (!live.length || !cached.length) return live;
  const byId = new Map(cached.map((room) => [room.roomId || room._id, room]));
  let usedCache = false;
  const merged = live.map((room) => {
    const hasPreview =
      room.lastTimestamp > 0 && room.message && room.message !== EMPTY_PREVIEW;
    if (hasPreview) return room;
    const prev = byId.get(room.roomId) || byId.get(room._id);
    if (!prev?.message || prev.message === EMPTY_PREVIEW) return room;
    usedCache = true;
    return {
      ...room,
      message: prev.message,
      time: prev.time || room.time,
      lastTimestamp: prev.lastTimestamp || room.lastTimestamp,
      lastMessageSenderId: room.lastMessageSenderId || prev.lastMessageSenderId,
    };
  });
  return usedCache ? sortRoomsByTimestamp(merged) : merged;
}

/**
 * Custom hook to manage Matrix rooms
 * @param {Object} params - Hook parameters
 * @param {Object} params.client - Matrix client instance
 * @param {boolean} params.isInitialized - Whether Matrix client is initialized
 * @param {Object} params.matrixUserInfo - Matrix user info object
 * @param {Object} params.options - Additional options
 * @param {number} params.options.loadingTimeout - Timeout in ms for loading state (default: 5000)
 * @param {number} params.options.fallbackTimeout - Fallback timeout in ms (default: 3000)
 * @returns {Object} Hook return value with rooms, loading state, error, and refresh function
 */
export const useMatrixRooms = ({
  client,
  isInitialized,
  matrixUserInfo,
  options = {}
}) => {
  const {
    loadingTimeout = 5000,
    fallbackTimeout = 3000
  } = options;

  const [rooms, setRooms] = useState(() => readInbox());
  const [loading, setLoading] = useState(() => readInbox().length === 0);
  const [error, setError] = useState(null);
  const [loadingState, setLoadingState] = useState('initializing'); // 'initializing' | 'syncing' | 'loading' | 'ready' | 'error'
  const myUserId = matrixUserInfo?.matrix_user_id;

  const [timelineReady, setTimelineReady] = useState(() =>
    typeof Matrix.getTimelineReady === 'function' ? Matrix.getTimelineReady() : true,
  );

  const mountedRef = useRef(true);
  const isSyncedRef = useRef(false);
  const timeoutRef = useRef(null);
  const syncListenerRef = useRef(null);
  const roomTimelineListenerRef = useRef(null);
  const roomMembershipListenerRef = useRef(null);
  const roomMemberMembershipListenerRef = useRef(null);
  const eventDecryptedListenerRef = useRef(null);
  const roomReceiptListenerRef = useRef(null);
  const roomAccountDataListenerRef = useRef(null);
  const roomSummaryListenerRef = useRef(null);
  const roomStateMembersListenerRef = useRef(null);
  const refreshTimeoutRef = useRef(null);

  const showWaitingInbox = useCallback((live) => {
    rememberInbox(live);
    const shown = live.length > 0 ? live : readInbox();
    setRooms(shown);
    setLoading(shown.length === 0);
    setLoadingState(shown.length > 0 ? 'ready' : 'syncing');
  }, []);

  const shouldDeferLoadingFallback = useCallback(() => {
    if (readInbox().length > 0) return false;
    if (typeof Matrix.getTimelineReady === 'function' && !Matrix.getTimelineReady()) return true;
    const st = client?.getSyncState?.();
    return st === SYNC_STATE_CATCHUP || st === 'RECONNECTING';
  }, [client]);

  const buildRoomsSnapshot = useCallback(() => {
    if (!client || !myUserId) return [];
    try {
      const raw = client.getRooms?.() || [];
      const roomsArray = Array.isArray(raw) ? [...raw] : [];
      if (!roomsArray.length) return [];
      const roomsData = roomsArray
        .map((room) => convertRoomToUserFormat(room, client, myUserId))
        .filter((room) => room !== null);
      return sortRoomsByTimestamp(roomsData);
    } catch (err) {
      captureChatException(err, { tags: { feature: 'matrix-rooms', action: 'snapshot' } });
      return [];
    }
  }, [client, myUserId]);

  /**
   * Fetch and process Matrix rooms
   */
  const fetchRooms = useCallback(() => {
    if (!mountedRef.current) return;

    if (!client || !myUserId) {
      setRooms(readInbox());
      setError(null);
      return;
    }

    const isTimelineReadyNow =
      typeof Matrix.getTimelineReady === 'function' ? Matrix.getTimelineReady() : true;

    try {
      setLoadingState(prev => prev === 'ready' ? prev : 'loading');
      const previousCached = readInbox();
      const cached = pruneAbandonedInboxEntries(previousCached, client, myUserId);
      if (cached.length !== previousCached.length) {
        if (cached.length > 0) Matrix.setInboxListSnapshot(cached);
        else Matrix.clearInboxListSnapshot();
      }
      const live = withCachedPreviews(buildRoomsSnapshot(), cached);
      const syncState = client.getSyncState?.();
      // After PREPARED, getRooms() is the inbox (limited timeline + m.heroes).
      // Keep the cached list until a non-empty live snapshot — do not wipe on
      // PREPARED-with-empty-store (fresh initial /sync) or before members/heroes load.
      const initialSyncDone =
        isTimelineReadyNow ||
        syncState === SYNC_STATE_PREPARED ||
        syncState === SYNC_STATE_SYNCING;

      if (live.length > 0) {
        rememberInbox(live);
        setRooms(live);
        setLoading(false);
        setLoadingState('ready');
        setError(null);
        return;
      }

      const rawRoomCount = (client.getRooms?.() || []).length;
      const confirmedEmpty =
        initialSyncDone && isTimelineReadyNow && rawRoomCount === 0;

      if (!confirmedEmpty) {
        showWaitingInbox(live);
        setError(null);
        return;
      }

      Matrix.clearInboxListSnapshot();
      setRooms([]);
      setLoadingState('ready');
      setError(null);
      setLoading(false);
    } catch (err) {
      captureChatException(err, { tags: { feature: 'matrix-rooms', action: 'fetch' } });
      const cached = readInbox();
      setRooms(cached);
      setError(err);
      setLoadingState(cached.length > 0 ? 'ready' : 'error');
      setLoading(cached.length === 0);
    }
  }, [buildRoomsSnapshot, client, myUserId, showWaitingInbox]);

  /**
   * Safe wrapper to fetch rooms only after sync is ready
   */
  const fetchRoomsSafe = useCallback(() => {
    if (!mountedRef.current) return;
    if (!isSyncedRef.current) return;
    fetchRooms();
  }, [fetchRooms]);

  /**
   * Debounced version of fetchRoomsSafe to prevent excessive refreshes
   * Refreshes rooms list after a short delay, canceling previous pending refreshes
   */
  const fetchRoomsSafeDebounced = useCallback(() => {
    if (!mountedRef.current) return;
    if (!isSyncedRef.current) return;

    // Clear any pending refresh
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Schedule a refresh after a short delay (300ms)
    refreshTimeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) {
        refreshTimeoutRef.current = null;
        return;
      }
      fetchRoomsSafe();
      refreshTimeoutRef.current = null;
    }, 300);
  }, [fetchRoomsSafe]);

  /**
   * Handle room timeline updates (new messages)
   */
  const handleRoomTimeline = useCallback((event, room, toStartOfTimeline) => {
    if (toStartOfTimeline) {
      fetchRoomsSafeDebounced();
      return;
    }
    if (!event) return;

    const shouldRefresh =
      shouldRefreshRoomsForTimelineEvent(event) ||
      event.isEncrypted?.() === true;

    if (shouldRefresh) {
      fetchRoomsSafeDebounced();
    }
  }, [fetchRoomsSafeDebounced]);

  /**
   * Handle decrypted events (important for encrypted rooms: message body becomes available after decryption)
   */
  const handleEventDecrypted = useCallback((event, err) => {
    if (err) return;
    if (!event) return;
    if (!isSyncedRef.current) return;

    if (shouldRefreshRoomsForTimelineEvent(event)) {
      fetchRoomsSafeDebounced();
    }
  }, [fetchRoomsSafeDebounced]);

  /**
   * Handle read receipts updates.
   * When we mark a room as read, Matrix updates the room's receipt state and unread counts,
   * but that does not emit a Room.timeline event. We refresh the room list here so badges clear.
   */
  const handleRoomReceipt = useCallback((event) => {
    if (!isSyncedRef.current) return;
    const content = event?.getContent?.() || event?.content || event?.event?.content;

    // If we can’t confidently filter, refresh (safe default).
    if (!myUserId || !content || typeof content !== 'object') {
      fetchRoomsSafeDebounced();
      return;
    }

    // Receipt content shape: { [eventId]: { [receiptType]: { [userId]: { ts, ... } } } }
    const hasMyReceipt = Object.values(content).some((byType) => {
      if (!byType || typeof byType !== 'object') return false;
      return Object.values(byType).some((byUser) => {
        if (!byUser || typeof byUser !== 'object') return false;
        return Boolean(byUser[myUserId]);
      });
    });

    if (hasMyReceipt) fetchRoomsSafeDebounced();
  }, [fetchRoomsSafeDebounced, myUserId]);

  /**
   * Handle per-room account data updates (e.g. m.fully_read).
   * Some homeservers/SDK flows update unread state via account data rather than timeline.
   */
  const handleRoomAccountData = useCallback((event) => {
    if (!isSyncedRef.current) return;
    const type = event?.getType?.() || event?.type;
    if (type === 'm.fully_read') {
      fetchRoomsSafeDebounced();
    }
  }, [fetchRoomsSafeDebounced]);

  /**
   * Lazy member load + sync summary updates: list entries must refresh when the SDK
   * finishes hydrating members/heroes (lazyLoadMembers) without a timeline event.
   */
  const handleRoomMembersOrSummary = useCallback(() => {
    fetchRoomsSafeDebounced();
  }, [fetchRoomsSafeDebounced]);

  /**
   * Handle room member membership changes (invitations, joins, etc.)
   */
  const handleRoomMemberMembership = useCallback((event, member) => {
    if (member && member.userId) {
      fetchRoomsSafeDebounced();
    }
  }, [fetchRoomsSafeDebounced]);

  /**
   * Handle our own membership changes in rooms (for room joins/invitations)
   */
  const handleMyMembership = useCallback((room, membership, prevMembership) => {
    // Refresh when we join a new room or get invited
    if (membership === 'join' || membership === 'invite') {
      fetchRoomsSafeDebounced();
    }
  }, [fetchRoomsSafeDebounced]);

  /**
   * Handle sync state changes
   */
  const handleSync = useCallback((state, prevState) => {
    // PREPARED means initial sync completed successfully
    if (state === SYNC_STATE_PREPARED) {
      isSyncedRef.current = true;
      setLoadingState('syncing');
      // PREPARED is enough for the inbox; fetchRooms keeps the cached list if live is empty.
      fetchRoomsSafe();
    }
    // SYNCING means client is actively syncing (happens after PREPARED)
    else if (state === SYNC_STATE_SYNCING) {
      // When the client recovers from ERROR/RECONNECTING/CATCHUP, it can transition back
      // into SYNCING. We refresh rooms on any transition into SYNCING (not only PREPARED)
      // to avoid "new message only appears after refresh" reports.
      isSyncedRef.current = true;
      if (prevState !== SYNC_STATE_SYNCING) {
        setLoadingState('syncing');
        fetchRoomsSafe();
      }
    }
    // CATCHUP means reconnecting and catching up
    else if (state === SYNC_STATE_CATCHUP) {
      const roomsArray = client?.getRooms();
      if (roomsArray && roomsArray.length > 0) {
        isSyncedRef.current = true;
        fetchRoomsSafe();
      }
    }
  }, [client, fetchRoomsSafe]);

  /**
   * Refresh rooms manually
   */
  const refreshRooms = useCallback(() => {
    if (mountedRef.current && client && isInitialized && myUserId) {
      fetchRooms();
    }
  }, [client, isInitialized, myUserId, fetchRooms]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (typeof Matrix.subscribeTimelineReady !== 'function') return undefined;
    return Matrix.subscribeTimelineReady(setTimelineReady);
  }, []);

  useEffect(() => {
    if (!isInitialized || !client || !myUserId) return;
    if (timelineReady && !isSyncedRef.current) return;
    fetchRooms();
  }, [timelineReady, isInitialized, client, myUserId, fetchRooms]);

  /**
   * Setup event listeners and initialize rooms
   */
  useEffect(() => {
    if (!isInitialized || !client || !myUserId) {
      const cached = readInbox();
      setRooms(cached);
      setLoading(cached.length === 0);
      setLoadingState(cached.length > 0 ? 'ready' : 'initializing');
      setError(null);
      return;
    }

    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const safe = (fn, label) => (...args) => {
      try {
        return fn(...args);
      } catch (e) {
        captureChatException(e, { tags: { feature: 'matrix-rooms', matrix: 'listener' }, extra: { label } });
        return undefined;
      }
    };

    // Set up event listeners for real-time updates
    roomTimelineListenerRef.current = safe(handleRoomTimeline, 'Room.timeline');
    roomMembershipListenerRef.current = safe(handleMyMembership, 'Room.myMembership');
    roomMemberMembershipListenerRef.current = safe(handleRoomMemberMembership, 'RoomMember.membership');
    syncListenerRef.current = safe(handleSync, 'sync');
    eventDecryptedListenerRef.current = safe(handleEventDecrypted, 'Event.decrypted');
    roomReceiptListenerRef.current = safe(handleRoomReceipt, 'Room.receipt');
    roomAccountDataListenerRef.current = safe(handleRoomAccountData, 'Room.accountData');
    roomStateMembersListenerRef.current = safe(handleRoomMembersOrSummary, 'RoomState.members');
    roomSummaryListenerRef.current = safe(handleRoomMembersOrSummary, 'Room.Summary');

    // Listen to timeline events from all rooms (for new messages)
    client.on('Room.timeline', roomTimelineListenerRef.current);

    // Lazy-loaded membership + room summary (heroes) — keeps DM list in sync without timeline noise
    client.on(RoomStateEvent.Members, roomStateMembersListenerRef.current);
    client.on(RoomEvent.Summary, roomSummaryListenerRef.current);

    // Listen to decrypted events (encrypted rooms)
    client.on('Event.decrypted', eventDecryptedListenerRef.current);

    // Listen to read receipt updates (unread badge clearing)
    client.on('Room.receipt', roomReceiptListenerRef.current);

    // Listen to per-room account data updates (m.fully_read etc.)
    client.on('Room.accountData', roomAccountDataListenerRef.current);

    // Listen to our own membership changes in rooms (for room joins/invitations)
    client.on('Room.myMembership', roomMembershipListenerRef.current);

    // Listen to room member membership changes (for invitations from others)
    client.on('RoomMember.membership', roomMemberMembershipListenerRef.current);

    // Listen to sync state changes
    client.on('sync', syncListenerRef.current);

    // Check if client is already synced on mount
    const currentSyncState = client.getSyncState();
    const roomsAvailable = client.getRooms();
    const loadingFallbackDeadline = Date.now() + LOADING_FALLBACK_MAX_WAIT_MS;

    const scheduleDeferredTimeout = (delayMs, onFire) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      const tick = () => {
        if (!mountedRef.current) return;
        const pastDeadline = Date.now() >= loadingFallbackDeadline;
        if (!pastDeadline && shouldDeferLoadingFallback()) {
          timeoutRef.current = setTimeout(tick, delayMs);
          return;
        }
        onFire();
      };
      timeoutRef.current = setTimeout(tick, delayMs);
    };

    const shouldFinalizeDeferredLoading = () => {
      const isTimelineReadyNow =
        timelineReady ||
        (typeof Matrix.getTimelineReady === 'function' ? Matrix.getTimelineReady() : true);
      return isTimelineReadyNow || Date.now() >= loadingFallbackDeadline;
    };

    // First, check if rooms are already available (may be cached from previous session)
    if (roomsAvailable && roomsAvailable.length > 0) {
      isSyncedRef.current = true;
      setLoadingState(prev => prev === 'ready' ? prev : 'loading');
      fetchRoomsSafe();
      // Unblock the spinner after the defer cap if sync is still slow.
      scheduleDeferredTimeout(fallbackTimeout, () => {
        fetchRoomsSafe();
        if (shouldFinalizeDeferredLoading()) {
          setLoading(false);
          setLoadingState('ready');
        }
      });
    } else if (currentSyncState === SYNC_STATE_PREPARED ||
      currentSyncState === SYNC_STATE_SYNCING ||
      currentSyncState === SYNC_STATE_CATCHUP) {
      // Client is in a synced state but no rooms yet
      isSyncedRef.current = true;
      setLoadingState(prev => prev === 'ready' ? prev : 'loading');
      fetchRoomsSafe();

      scheduleDeferredTimeout(fallbackTimeout, () => {
        const roomsArray = client.getRooms();
        if (roomsArray && roomsArray.length > 0) {
          isSyncedRef.current = true;
          fetchRoomsSafe();
        }
        if (shouldFinalizeDeferredLoading()) {
          setLoading(false);
          setLoadingState('ready');
        }
      });
    } else {
      // Wait for sync to complete, but add a fallback timeout
      setLoading(true);
      setLoadingState('initializing');
      scheduleDeferredTimeout(loadingTimeout, () => {
        const roomsArray = client.getRooms();
        if (roomsArray && roomsArray.length > 0) {
          isSyncedRef.current = true;
          fetchRoomsSafe();
        }
        if (shouldFinalizeDeferredLoading()) {
          setLoading(false);
          setLoadingState('ready');
        }
      });
    }

    // Cleanup event listeners and timeouts
    return () => {
      isSyncedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      if (client) {
        if (roomTimelineListenerRef.current) {
          client.removeListener('Room.timeline', roomTimelineListenerRef.current);
        }
        if (roomMembershipListenerRef.current) {
          client.removeListener('Room.myMembership', roomMembershipListenerRef.current);
        }
        if (roomMemberMembershipListenerRef.current) {
          client.removeListener('RoomMember.membership', roomMemberMembershipListenerRef.current);
        }
        if (syncListenerRef.current) {
          client.removeListener('sync', syncListenerRef.current);
        }
        if (eventDecryptedListenerRef.current) {
          client.removeListener('Event.decrypted', eventDecryptedListenerRef.current);
        }
        if (roomReceiptListenerRef.current) {
          client.removeListener('Room.receipt', roomReceiptListenerRef.current);
        }
        if (roomAccountDataListenerRef.current) {
          client.removeListener('Room.accountData', roomAccountDataListenerRef.current);
        }
        if (roomStateMembersListenerRef.current) {
          client.removeListener(RoomStateEvent.Members, roomStateMembersListenerRef.current);
        }
        if (roomSummaryListenerRef.current) {
          client.removeListener(RoomEvent.Summary, roomSummaryListenerRef.current);
        }
      }
    };
  }, [
    isInitialized,
    client,
    myUserId,
    handleRoomTimeline,
    handleEventDecrypted,
    handleRoomReceipt,
    handleRoomAccountData,
    handleRoomMembersOrSummary,
    handleMyMembership,
    handleRoomMemberMembership,
    handleSync,
    fetchRoomsSafe,
    fetchRoomsSafeDebounced,
    shouldDeferLoadingFallback,
    timelineReady,
    fallbackTimeout,
    loadingTimeout,
  ]);

  return {
    rooms,
    loading,
    loadingState,
    error,
    refreshRooms
  };
};
